import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Key, RefreshCw } from 'lucide-react';

/**
 * Form for super-admin to generate a new USIC or CSIC subscription code.
 * Behaviour preserved from the original inline implementation in AdminPage.js.
 */
export default function GenerateCodeForm({
    newCode,
    setNewCode,
    codes,
    generating,
    onGenerate,
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-teal-600" />
                    Generate New Code
                </CardTitle>
                <CardDescription>
                    Create a new USIC (User) or CSIC (Company) subscription code
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="grid gap-6 max-w-md">
                    <div className="space-y-2">
                        <Label>Code Type</Label>
                        <Select
                            value={newCode.code_type}
                            onValueChange={(v) => setNewCode({ ...newCode, code_type: v })}
                        >
                            <SelectTrigger data-testid="code-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="USIC">USIC - User Subscription</SelectItem>
                                <SelectItem value="CSIC">CSIC - Organisation Subscription</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {newCode.code_type === 'USIC' && (
                        <div className="space-y-2">
                            <Label>Organisation (CSIC) *</Label>
                            <Select
                                value={newCode.parent_csic}
                                onValueChange={(v) => setNewCode({ ...newCode, parent_csic: v })}
                            >
                                <SelectTrigger data-testid="parent-csic-select">
                                    <SelectValue placeholder="Select the organisation this user belongs to" />
                                </SelectTrigger>
                                <SelectContent>
                                    {codes
                                        .filter((c) => c.code_type === 'CSIC' && c.is_active)
                                        .map((c) => (
                                            <SelectItem key={c.id} value={c.code}>
                                                {c.organization_name || 'Unnamed'} — {c.code}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                If the organisation isn&apos;t listed, generate its CSIC first.
                            </p>
                        </div>
                    )}

                    {newCode.code_type === 'CSIC' && (
                        <div className="space-y-2">
                            <Label>Organisation Name *</Label>
                            <Input
                                placeholder="e.g., NHS Trust London"
                                value={newCode.organization_name}
                                onChange={(e) => setNewCode({ ...newCode, organization_name: e.target.value })}
                                data-testid="org-name-input"
                            />
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>Validity Period (Days)</Label>
                        <Select
                            value={newCode.expires_days.toString()}
                            onValueChange={(v) => setNewCode({ ...newCode, expires_days: parseInt(v, 10) })}
                        >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="30">30 days</SelectItem>
                                <SelectItem value="90">90 days</SelectItem>
                                <SelectItem value="180">180 days (6 months)</SelectItem>
                                <SelectItem value="365">365 days (1 year)</SelectItem>
                                <SelectItem value="730">730 days (2 years)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <Button
                        onClick={onGenerate}
                        disabled={generating}
                        className="bg-teal-600 hover:bg-teal-700"
                        data-testid="generate-code-btn"
                    >
                        {generating
                            ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            : <Key className="w-4 h-4 mr-2" />}
                        Generate Code
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
